import React, { useMemo } from "react";
import { MdClose, MdKeyboardArrowDown, MdFilterList, MdSort } from "react-icons/md";
// CHANGED: Import Menu icon for the filter button
import { Menu } from "lucide-react";

const FilterDisplay = ({
  totalCars = 0,
  filteredCars = 0,
  selectedFilters = {},
  onRemoveFilter,
  onClearAllFilters,
  sortBy,
  onSortChange,
  // CHANGED: Prop to trigger the filter menu drawer
  onMobileFilterOpen,
  sortOptions = [
    { value: "most-popular", label: "Most popular" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "price-high", label: "Price: High to Low" },
  ],
}) => {
  const activeFilters = useMemo(() => {
    return Object.entries(selectedFilters || {})
      .filter(([_, values]) => Array.isArray(values) && values.length > 0)
      .flatMap(([category, values]) => {
        return values
          .filter((value) => {
            if (category !== "Car Type") return true;
            if (!value.includes(":")) return true; // keep parents always
            const parent = value.split(":")[0];
            return !values.includes(parent); // hide child if parent is selected
          })
          .map((value) => ({ category, value }));
      });
  }, [selectedFilters]);

  if (activeFilters.length === 0 && totalCars === 0) return null;

  return (
    <div className="sticky top-0 z-30 border-navy/5">
      <div className="mx-auto max-w-7xl py-3 px-2">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {/* Left: counts + chips */}
          <div className="flex flex-col gap-2">
            {totalCars > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="inline-flex h-2 w-2 rounded-full bg-gold shadow-gold-glow animate-pulse" />
                <span className="text-navy/60 font-heading font-medium">
                  Showing{" "}
                  <span className="font-semibold text-navy font-bold">{filteredCars}</span> of{" "}
                  <span className="font-semibold text-navy font-bold">{totalCars}</span> cars
                </span>
              </div>
            )}

            {activeFilters.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-navy/40 tracking-[0.2em] font-black">
                  <MdFilterList size={16} />
                  Filters
                </div>

                {activeFilters.map((f, idx) => {
                  const label =
                    f.category === "Pickup Location Type" || f.category === "Return Location Type"
                      ? `${f.category}: ${f.value}`
                      : f.value.includes(":") ? f.value.split(":")[1] : f.value;

                  return (
                    <span
                      key={`${f.category}-${f.value}-${idx}`}
                      className="group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium border-navy/10 bg-white text-navy font-heading font-bold shadow-sm hover:border-gold/30"
                    >
                      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-gold" />
                      <span className="max-w-[220px] truncate">{label}</span>
                      <button
                        onClick={() => onRemoveFilter?.(f.category, f.value)}
                        className="text-navy/30 hover:bg-navy/5 hover:text-navy"
                        aria-label={`Remove ${label}`}
                        type="button"
                      >
                        <MdClose size={16} />
                      </button>
                    </span>
                  );
                })}

                <button
                  type="button"
                  onClick={onClearAllFilters}
                  className="ml-1 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold bg-navy text-white hover:bg-navy-light font-heading font-black shadow-premium"
                >
                  Clear All
                  <MdClose className="text-gold" size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Right: sort + Mobile Filter Button */}
          {totalCars > 0 && (
            // CHANGED: Layout for single row on mobile, keeps original layout on desktop
            <div className="flex flex-row items-center gap-2 w-full lg:w-auto lg:justify-end">
              
              {/* FILTER BUTTON (Mobile Only) */}
              <button 
                onClick={onMobileFilterOpen}
                className="lg:hidden flex-1 flex items-center justify-center gap-2 rounded-xl border border-navy/5 bg-white text-navy font-heading font-black shadow-premium px-3 h-12 shadow-sm active:bg-slate-50 transition-colors"
              >
                <Menu size={16} className="text-gold"/>
                <span className="text-[11px] uppercase tracking-widest">Filter</span>
                {activeFilters.length > 0 && (
                  <span className="bg-navy text-white text-[9px] w-4 h-4 flex items-center justify-center rounded-full ml-1">
                    {activeFilters.length}
                  </span>
                )}
              </button>

              {/* SORT BUTTON (Desktop + Mobile) */}
              {/* CHANGED: Adjusted classes to make this look like a button on mobile and a dropdown on desktop */}
              <div className="relative flex-1 lg:flex-initial flex items-center justify-center gap-2 rounded-xl border border-navy/5 bg-white text-navy font-heading font-black shadow-premium px-3 h-12 shadow-sm">
              <div className="pointer-events-none flex items-center gap-2 overflow-hidden">
                <MdSort size={18} className="text-gold shrink-0"/>
                <span className="text-[11px] uppercase tracking-widest text-slate-700 hidden lg:inline">Sort</span>
                <span className="text-[11px] font-bold text-navy truncate lg:hidden">
                  {sortOptions.find(o => o.value === sortBy)?.label || "Sort"}
                </span>
              </div>

                {/* The actual select is invisible but fills the button area on mobile */}

                <select
                  value={sortBy}
                  onChange={(e) => onSortChange?.(e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 lg:opacity-100 lg:relative lg:w-auto lg:h-auto lg:appearance-none lg:bg-transparent lg:pl-2 lg:pr-8 lg:text-sm lg:font-bold lg:text-navy cursor-pointer outline-none border p-2 rounded-lg "
                >
                  {sortOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>

                <MdKeyboardArrowDown
                  size={20}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gold lg:block"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterDisplay;